/**
 * `useCalcMembers` — pure, context-free transforms between the Calculated-KPI builder's segment
 * state and the backend `calc_members` wire shape, plus the page-shape helpers that decide which
 * dimensions a pin picker may offer and which the page leaves open.
 *
 * Everything here is a plain function (no React state, no React Query) so it unit-tests without a
 * provider tree. Callers supply the inputs: `pageLayout` (from `useGetPageLayout`) and `page_drivers`
 * (from `usePageContext`).
 *
 * See `design-docs/page-editor/calculated-kpis.md` (Data model / Backend integration).
 */
import { DimSumEnhancedNode } from '@amzn/gft-dimsum-service';
import {
  BuildCalcMembersArgs,
  CalcDisplayToken,
  SegmentsToCalcMembersArgs,
} from '@deckManagement/types/CalcMembers.types';
import { KpiFormulaNode, KpiFormulaNodeType } from '@deckManagement/types/CalculatedKPIBuilder.types';
import {
  CalcExpression,
  CalcExpressionInput,
  CalcPinInput,
  CalcTokenInput,
  CalcTokenKind,
  DatasetSource,
  GalaxyKPIDrivers,
} from '@services/api/deckManagement/graphql/schema/API';
import { splitNodeId } from '@utils/kpiDriverHelpers';
import { isNil } from 'lodash';
import { HeaderTreePageLayout } from 'types/Page.types';
import { KpiDimension } from 'types/PageSidePanel.types';

/**
 * The operators the backend can resolve. Mirrors the narrowed builder/parser operator set
 * (`+ - ( )`); `*` and `/` are rejected server-side and are not authorable (design §5.3, R6).
 */
const SUPPORTED_OPERATORS = new Set(['+', '-', '(', ')']);

/**
 * The wire `{ value, hierarchy }` pair for a NODE token, from the segment's resolved node.
 *
 * A resolved node's `nodeID` is the FULL id (`<HIERARCHY>-<node>`); the backend wants them SPLIT
 * (`value` = bare node, `hierarchy` = prefix) — sending the joined id as `value` fails the backend's
 * driver-completeness gate. Falls back to the segment's raw `value` with no hierarchy when no node is
 * attached (a hand-typed code). Delegates the split to the shared {@link splitNodeId}.
 *
 * @param segment a builder segment of type NODE
 */
const nodeValueAndHierarchy = (segment: KpiFormulaNode): { value: string; hierarchy?: string } => {
  const nodeID = segment.dimSumNode?.nodeID;
  if (!nodeID) {
    return { value: segment.value };
  }
  const { value, hierarchy } = splitNodeId(nodeID);
  return hierarchy ? { value, hierarchy } : { value };
};

/**
 * Serializes the builder's segments into a {@link CalcExpressionInput} (design §3.1, R2.1, R6).
 *
 * - `type: Operator` → `{ kind: OPERATOR, value }`, keeping only `+ - ( )` (a stray `*`/`/` is
 *   dropped defensively — it is unauthorable once the operator set is narrowed).
 * - `type: Node` → `{ kind: NODE, value: <bare node id>, hierarchy: <node hierarchy> }` — the node's
 *   `nodeID` is SPLIT into the bare value and its hierarchy (see {@link nodeValueAndHierarchy}).
 * - `type: Unknown`/blank segments (builder insertion-point placeholders) are filtered out.
 *
 * `hierarchy` is taken from the attached node the author picked, so each NODE token carries its
 * DimSum hierarchy at serialize time; the save path sends the authored expression verbatim.
 */
export const segmentsToCalcMembers = (args: SegmentsToCalcMembersArgs): CalcExpressionInput => {
  const { segments, dimension, datasetSource, pins } = args;

  const tokens: CalcTokenInput[] = [];
  segments.forEach((segment) => {
    if (segment.type === KpiFormulaNodeType.Operator) {
      if (SUPPORTED_OPERATORS.has(segment.value)) {
        tokens.push({ kind: CalcTokenKind.OPERATOR, value: segment.value });
      }
      return;
    }
    if (segment.type === KpiFormulaNodeType.Node) {
      const { value, hierarchy } = nodeValueAndHierarchy(segment);
      tokens.push({
        kind: CalcTokenKind.NODE,
        value,
        ...(hierarchy ? { hierarchy } : {}),
      });
    }
    // Unknown / blank placeholders are intentionally skipped.
  });

  // Pins on the expression dimension are dropped (that dimension is supplied by the tokens). Column-
  // owned pins are already excluded upstream by `buildCalcMembers`.
  const safePins = (pins ?? []).filter((pin) => pin.dimension !== dimension);

  return {
    dimension,
    tokens,
    ...(safePins.length > 0 ? { pins: safePins } : {}),
    dataset_source: datasetSource ?? DatasetSource.GALAXY,
  };
};

/**
 * Assembles the panel's per-dimension authored state into a {@link CalcExpressionInput} (design §4.2,
 * handoff §3/§6b).
 *
 * This is the AUTHORING path, where the hierarchy is already known from the nodes the user picked, so
 * nothing is re-resolved at save:
 * - the **expression dimension**'s segments become NODE/OPERATOR tokens, each NODE carrying its
 *   `dimSumNode` hierarchy (via {@link segmentsToCalcMembers});
 * - every **pinned** dimension (an open dimension the user set to a single node) becomes a
 *   `{ dimension, node, hierarchy }` pin, the hierarchy read from the picked node's `attributes.id`;
 * - **column-owned** dimensions and the expression dimension itself are never pinned (handoff §6b).
 *
 * Returns `null` when there is no expression dimension yet (nothing to serialize).
 *
 * @param args {@link BuildCalcMembersArgs}
 */
export const buildCalcMembers = (args: BuildCalcMembersArgs): CalcExpressionInput | null => {
  const { expressionDimension, segments, pinnedNodes, columnOwnedDimensions: columnOwned, datasetSource } = args;
  if (isNil(expressionDimension) || expressionDimension === '') {
    return null;
  }

  const pins: CalcPinInput[] = Object.entries(pinnedNodes)
    .filter(([dimension]) => dimension !== expressionDimension && !columnOwned.has(dimension))
    .map(([dimension, node]) => {
      const { value, hierarchy } = splitNodeId(node.nodeID);
      return {
        dimension,
        node: value,
        ...(hierarchy ? { hierarchy } : {}),
      };
    });

  return segmentsToCalcMembers({ segments, dimension: expressionDimension, datasetSource, pins });
};

/**
 * Produces the ordered display tokens for the read-only expression rendering (design §3.2, R1.1).
 *
 * NODE tokens render by their resolved name when `nameFor` supplies one, else fall back to the raw
 * value (R1.5). OPERATOR tokens render verbatim. `null` input → `null`.
 *
 * @param calc the served `calc_members`, or `null`
 * @param nameFor optional resolver from a node id to a display name
 */
export const calcMembersToDisplayTokens = (
  calc: CalcExpression | null | undefined,
  nameFor?: (nodeId: string) => string | undefined,
): CalcDisplayToken[] | null => {
  if (isNil(calc)) {
    return null;
  }
  const tokens = calc.tokens ?? [];
  return tokens
    .filter((token): token is NonNullable<typeof token> => !isNil(token))
    .map((token) => {
      const value = token.value ?? '';
      if (token.kind === CalcTokenKind.OPERATOR) {
        return { kind: 'OPERATOR', display: value, value };
      }
      return { kind: 'NODE', display: nameFor?.(value) || value, value };
    });
};

/**
 * Reconstructs the builder's `KpiFormulaNode[]` segment array from a served `calc_members` expression,
 * so the panel can SEED `expressionSegments` for a served supported row. Operator tokens become
 * `Operator` segments. A NODE token WITH a hierarchy becomes a `Node` segment whose minimal
 * `dimSumNode.nodeID` is the `<hierarchy>-<value>` id — the exact shape {@link nodeValueAndHierarchy}
 * reads back on re-serialize.
 *
 * A NODE token WITHOUT a hierarchy becomes a `Node` segment with NO `dimSumNode`, carrying the bare
 * value. This is deliberate: if we stored `dimSumNode.nodeID = value`, re-serialize would re-split it
 * on the first `-` and fabricate a bogus hierarchy for a value that legitimately contains a dash (e.g.
 * `A-B`). With no `dimSumNode`, `nodeValueAndHierarchy` takes its raw-value fallback and the bare value
 * round-trips intact.
 *
 * Without this seed, a pin-only edit on a served row re-emits with `expressionSegments === []`, which
 * serializes empty `tokens` and drops the authored expression.
 *
 * @param calcMembers the served expression, or null/undefined
 * @returns the reconstructed segments, or an empty array when there is nothing to seed
 */
export const calcMembersToSegments = (calcMembers: CalcExpression | null | undefined): KpiFormulaNode[] => {
  if (isNil(calcMembers)) {
    return [];
  }
  return (calcMembers.tokens ?? [])
    .filter((token): token is NonNullable<typeof token> => !isNil(token))
    .map((token) => {
      const value = token.value ?? '';
      if (token.kind === CalcTokenKind.OPERATOR) {
        return { value, type: KpiFormulaNodeType.Operator };
      }
      const hierarchy = (token as { hierarchy?: string | null }).hierarchy;
      // No hierarchy → leave dimSumNode unset so re-serialize keeps the bare value verbatim (a value
      // containing a `-` must not be re-split). With a hierarchy, encode the full `<hierarchy>-<value>`
      // id so re-serialize splits it back to the same { value, hierarchy }.
      if (isNil(hierarchy) || hierarchy === '') {
        return { value, type: KpiFormulaNodeType.Node };
      }
      const nodeID = `${hierarchy}-${value}`;
      // Minimal node: only `nodeID`/`attributes.id` are read on re-serialize (via splitNodeId); the
      // display name falls back to the bare value, which the matrix already shows for served rows.
      const dimSumNode = {
        nodeID,
        attributes: { id: nodeID, name: value },
      } as DimSumEnhancedNode;
      return { value, type: KpiFormulaNodeType.Node, dimSumNode };
    });
};

/**
 * The set of GalaxyKPIDrivers value-keys (excluding the `*_hierarchy` companions and non-dimension
 * fields) that map to a {@link KpiDimension}.
 */
const KPI_DIMENSION_VALUE_KEYS = new Set<string>(Object.values(KpiDimension));

/**
 * Collects the value-set dimension keys from one leaf's `dimensionOverrides`.
 *
 * Ignores `__typename`, the `*_hierarchy` companions, and any key that is not a known dimension.
 */
const ownedKeysFromOverrides = (overrides: GalaxyKPIDrivers | null | undefined): KpiDimension[] => {
  if (isNil(overrides)) {
    return [];
  }
  const record = overrides as Record<string, string | null | undefined>;
  return Object.keys(record)
    .filter((key): key is KpiDimension => KPI_DIMENSION_VALUE_KEYS.has(key))
    .filter((key) => !isNil(record[key]) && record[key] !== '');
};

/**
 * Computes the page's column-owned dimension set (design §3.3, R4.1): the union, across every
 * section's flat `columns[]` projection, of dimension keys whose value is set in the leaf's
 * `dimensionOverrides`.
 *
 * @param pageLayout the page layout from `useGetPageLayout`, or undefined while loading
 */
export const columnOwnedDimensions = (pageLayout: HeaderTreePageLayout | undefined | null): Set<KpiDimension> => {
  const owned = new Set<KpiDimension>();
  if (isNil(pageLayout)) {
    return owned;
  }
  (pageLayout.sections ?? []).forEach((section) => {
    (section?.columns ?? []).forEach((column) => {
      ownedKeysFromOverrides(column?.dimensionOverrides).forEach((key) => owned.add(key));
    });
  });
  return owned;
};

/**
 * Whether a `calc_members` expression was AUTHORED (vs merely read from the backend): true only when
 * it has at least one NODE token AND every NODE token carries a non-empty `hierarchy`.
 *
 * The served `CalcToken` output type omits `hierarchy`, so a row loaded from `getDecklines` has
 * hierarchy-less NODE tokens; the authoring path (builder → `asCalcExpression`) attaches it to each
 * node the author picked. The save path uses this to decide whether a modified calc row's expression
 * is safe to send and to null its baked `kpi_id`/`kpi_formula` — a hierarchy-less expression is not
 * (it would fail the backend driver-completeness gate and lose the derivable id).
 *
 * @param calcMembers the entity's `calc_members`, or null/undefined
 * @returns true when every NODE token carries a hierarchy (and at least one NODE token exists)
 */
export const calcMembersHaveHierarchy = (calcMembers: CalcExpression | null | undefined): boolean => {
  if (isNil(calcMembers)) {
    return false;
  }
  const nodeTokens = (calcMembers.tokens ?? [])
    .filter((token): token is NonNullable<typeof token> => !isNil(token))
    .filter((token) => token.kind === CalcTokenKind.NODE);
  if (nodeTokens.length === 0) {
    return false;
  }
  return nodeTokens.every((token) => {
    const hierarchy = (token as { hierarchy?: string | null }).hierarchy;
    return !isNil(hierarchy) && hierarchy !== '';
  });
};
